import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Card, Typography, Button, CircularProgress,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ShowCard from './ShowCard';
import useStore from '../store';
import { getRecommendations } from '../utils/recommendationEngine';
import { fetchShowPoster } from '../services/showClient';
import { auth } from '../firebase';

const CACHE_KEY = 'bsb_recs_cache';
const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours

function loadCache(ratingCount) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { recommendations, cachedAt, ratingsCount } = JSON.parse(raw);
    if (Date.now() - cachedAt > CACHE_TTL) return null;
    if (Math.abs((ratingsCount ?? 0) - ratingCount) >= 3) return null;
    return recommendations?.length ? recommendations : null;
  } catch { return null; }
}

function saveCache(recommendations, ratingCount) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ recommendations, cachedAt: Date.now(), ratingsCount: ratingCount }));
  } catch { }
}


function SuggestionCard({ rec, onAddToWatchlist, onRate }) {
  const [expanded, setExpanded] = useState(false);
  const hasLongReason = rec.reason && rec.reason.length > 110;

  return (
    <Card sx={{ mb: 2, overflow: 'hidden', borderRadius: 2 }}>
      <ShowCard show={rec} hideCTA scoreLabel={rec.score} />

      {rec.reason && (
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontStyle: 'italic',
              fontFamily: 'serif',
              fontSize: '0.83rem',
              color: 'text.secondary',
              lineHeight: 1.55,
              ...(expanded ? {} : {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
              }),
            }}
          >
            "{rec.reason}"
          </Typography>
          {hasLongReason && (
            <Button
              size="small"
              onClick={() => setExpanded(v => !v)}
              sx={{
                textTransform: 'none',
                color: 'primary.main',
                fontSize: '0.72rem',
                p: 0,
                mt: 0.25,
                minWidth: 0,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              {expanded ? 'Read less' : 'Read more'}
            </Button>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, px: 2, pt: 1, pb: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          onClick={() => onAddToWatchlist(rec)}
          sx={{
            textTransform: 'none',
            fontSize: '0.8rem',
            borderColor: 'rgba(232,184,109,0.35)',
            color: 'primary.main',
            borderRadius: 1.5,
            '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(232,184,109,0.06)' },
          }}
        >
          + Add to list
        </Button>
        <Button
          fullWidth
          variant="contained"
          size="small"
          onClick={() => onRate(rec)}
          sx={{ textTransform: 'none', fontSize: '0.8rem', borderRadius: 1.5 }}
        >
          Rate this
        </Button>
      </Box>
    </Card>
  );
}

export default function SuggestScreen({ onRateShow }) {
  const shows = useStore(s => s.shows);
  const userRatings = useStore(s => s.userRatings);
  const setUserRating = useStore(s => s.setUserRating);
  const userPreferences = useStore(s => s.userPreferences);

  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasAutoLoaded = useRef(false);

  const load = useCallback(async (forceRefresh = false) => {
    const ratingCount = Object.keys(userRatings).length;

    if (!forceRefresh) {
      const cached = loadCache(ratingCount);
      if (cached) {
        setRecommendations(cached);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('Session expired — please sign in again.');
      const recs = await getRecommendations(shows, userRatings, userPreferences, idToken);

      const enriched = await Promise.all(
        recs.map(async (rec) => {
          if (rec.poster_path && rec.poster_path.startsWith('http') && !rec.poster_path.includes('placeholder')) {
            return rec;
          }
          try {
            const posterUrl = await fetchShowPoster(rec.title, rec.year);
            if (posterUrl) return { ...rec, poster_path: posterUrl, posterUrl };
          } catch (err) {
            console.error('[SuggestScreen] TMDB enrichment failed:', err);
          }
          return rec;
        })
      );

      const ratedIds = new Set(Object.keys(userRatings).map(String));
      const ratedTitles = new Set(Object.values(userRatings).map(r => (r.title || '').toLowerCase().trim()));
      const baseTitles = new Set(shows.map(s => (s.title || '').toLowerCase().trim()));

      const filtered = enriched.filter(r => {
        const titleLower = (r.title || '').toLowerCase().trim();
        return !(ratedIds.has(String(r.id)) || ratedTitles.has(titleLower) || baseTitles.has(titleLower));
      });

      saveCache(filtered, ratingCount);
      setRecommendations(filtered);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }, [shows, userRatings, userPreferences]);

  const fetchPosterForShow = async (show) => {
    try {
      const url = await fetchShowPoster(show.title, show.year);
      if (url) return url;
    } catch (err) {
      console.error('[SuggestScreen] TMDB fetch failed:', err);
    }
    return show.posterUrl || show.poster_path || '/poster_placeholder.svg';
  };

  const handleAddToWatchlist = async (show) => {
    setRecommendations(prev => prev.filter(r => String(r.id) !== String(show.id)));

    let enrichedShow = { ...show };
    if (!show.poster_path || show.poster_path.includes('placeholder')) {
      const posterUrl = await fetchPosterForShow(show);
      enrichedShow.posterUrl = posterUrl;
      enrichedShow.poster_path = posterUrl;
    }

    const uniqueId = `ai-${show.id}`;
    setUserRating(uniqueId, { showId: uniqueId, rating: 0, notes: '', dismissed: false, ...enrichedShow });
  };

  const handleRate = (show) => {
    onRateShow({
      ...show,
      onSave: () => {
        setRecommendations(prev => prev.filter(r => String(r.id) !== String(show.id)));
      },
    });
  };

  const hasFavorites = Object.values(userRatings).some(r => (r.rating ?? 0) >= 4);

  const refillCount = useRef(0);
  useEffect(() => {
    if (!loading && !error && recommendations.length === 0 && hasAutoLoaded.current && hasFavorites && refillCount.current < 3) {
      refillCount.current += 1;
      load();
    }
  }, [recommendations.length, loading, error, hasFavorites, load]);

  useEffect(() => {
    if (hasAutoLoaded.current) return;
    hasAutoLoaded.current = true;
    load();
  }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {recommendations.length > 0
            ? `The Butler's Dossier (${recommendations.length})`
            : 'Detective Recommendations'}
        </Typography>
        {recommendations.length > 0 && (
          <Button
            size="small"
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={() => load(true)}
            disabled={loading}
            sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem' }}
          >
            Consult Butler
          </Button>
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 8 }}>
          <SearchIcon sx={{ color: 'primary.main', fontSize: 48, mb: 1, animation: 'pulse 1.5s infinite ease-in-out' }} />
          <Typography variant="h6" sx={{ color: 'text.primary', fontFamily: 'serif' }}>
            The Butler is consulting the archives...
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', px: 4 }}>
            "One moment, sir. I'm combing through the archives for something that meets your exacting standards."
          </Typography>
          <CircularProgress sx={{ color: 'primary.main', mt: 2 }} size={24} />
          <style>
            {`@keyframes pulse { 0% { opacity: 0.4; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.1); } 100% { opacity: 0.4; transform: scale(0.9); } }`}
          </style>
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Box sx={{ textAlign: 'center', mt: 6, p: 3, bgcolor: 'rgba(211, 47, 47, 0.05)', borderRadius: 2 }}>
          <Typography sx={{ color: 'error.main', mb: 2, fontSize: '0.9rem', fontWeight: 500 }}>
            {error}
          </Typography>
          <Button variant="outlined" color="error" onClick={load} sx={{ textTransform: 'none' }}>Try again</Button>
        </Box>
      )}

      {/* Empty — no favorites yet */}
      {!loading && !error && recommendations.length === 0 && !hasFavorites && (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <AutoAwesomeIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
          <Typography sx={{ color: 'text.secondary', mb: 1 }}>
            "Ah, a fresh face. Allow me to prepare a 'Standard Inspection'."
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 3 }}>
            I'll suggest some essential British detective classics to get you started.
          </Typography>
          <Button variant="contained" onClick={load} sx={{ textTransform: 'none' }}>
            Kickstart Recommendations
          </Button>
        </Box>
      )}

      {/* Empty — suggestions exhausted */}
      {!loading && !error && recommendations.length === 0 && hasFavorites && (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>
            "Deepest apologies, sir. The archives appear to be momentarily depleted."
          </Typography>
          <Button variant="contained" onClick={load} sx={{ textTransform: 'none' }}>
            Try a deeper search
          </Button>
        </Box>
      )}

      {/* Recommendation cards */}
      {!loading && recommendations.length > 0 && recommendations.map(rec => (
        <SuggestionCard
          key={rec.id}
          rec={rec}
          onAddToWatchlist={handleAddToWatchlist}
          onRate={handleRate}
        />
      ))}
    </Box>
  );
}
